import React, { useContext, useReducer, useRef, useEffect, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { comboGroupReducer } from './comboGroupReducer';
import {
	ComboGroupContext,
	comboGroupInitialState,
	IFilterValues,
	TComboGroupCountOfItems,
	TComboGroupsSortBy,
	TTypeOfModalWindow,
} from './comboGroupContext';
import { ComboGroupActionTypes } from './comboGroupActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedComboGroups, deleteComboGroup } from 'backend/comboGroups';
import { IComboGroups, IGetPaginatedComboGroups } from 'types';
import { AxiosResponse } from 'axios';

const ComboGroupState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(comboGroupReducer, comboGroupInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IComboGroups>, IGetPaginatedComboGroups>(
		isMounted.current ? getPaginatedComboGroups : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer,
			restaurantId: state.IDSelectedRestaurant,
			dishId: state.IDSelectedDish,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ComboGroupActionTypes.SET_COMBO_GROUPS,
				payload: {
					comboGroups: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteComboGroup(state.IDForDeletion);
			const filteredComboGroups = state.comboGroups.comboGroups.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: ComboGroupActionTypes.SET_COMBO_GROUPS,
				payload: {
					comboGroups: {
						comboGroupsCount: state.comboGroups.comboGroupsCount - 1,
						comboGroups: filteredComboGroups,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: ComboGroupActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: ComboGroupActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const setIDCustomer = (value: Nullable<string>) => {
		dispatch({
			type: ComboGroupActionTypes.SET_ID_SELECTED_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: ComboGroupActionTypes.SET_ID_SELECTED_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	};

	const setIDDish = (value: Nullable<string>) => {
		dispatch({
			type: ComboGroupActionTypes.SET_ID_SELECTED_DISH,
			payload: {
				IDSelectedDish: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: ComboGroupActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TComboGroupsSortBy) => {
		dispatch({
			type: ComboGroupActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TComboGroupCountOfItems) => {
		dispatch({
			type: ComboGroupActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: ComboGroupActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: ComboGroupActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: ComboGroupActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: ComboGroupActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<ComboGroupContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setIDCustomer,
				setIDRestaurant,
				setIDDish,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				comboGroups: state.comboGroups,
				searchString: state.searchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedDish: state.IDSelectedDish,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ComboGroupContext.Provider>
	);
};

export default ComboGroupState;
